import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useSlugNavigate } from '../hooks/useNavigation'
import { listMigrations, deleteMigration, getTenancyRelationships } from '../api/migrations'
import MigrationForm from '../components/migrations/MigrationForm'
import DashboardLayout from '../components/DashboardLayout'

const MigrationsPage = () => {
  const { user } = useAuth()
  const navigate = useSlugNavigate()
  const [migrations, setMigrations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [managedClients, setManagedClients] = useState([])

  const hasChildren = managedClients.length > 0

  useEffect(() => {
    if (user?.active_tenancy_id) {
      loadMigrations()
      loadRelationships()
    } else {
      setLoading(false)
    }
  }, [user?.active_tenancy_id])

  const loadMigrations = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await listMigrations(user.active_tenancy_id)
      setMigrations(data.migrations || [])
    } catch (err) {
      console.error('Failed to load migrations:', err)
      setError(`Failed to load migrations: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const loadRelationships = async () => {
    try {
      const data = await getTenancyRelationships(user.active_tenancy_id)
      setManagedClients(data.managed_clients || [])
    } catch (err) {
      console.error('Failed to load relationships:', err)
    }
  }

  const handleMigrationCreated = (newMigration) => {
    // Navigate to the new migration's detail page
    navigate(`/migrations/${newMigration.id}`)
  }

  const handleDeleteMigration = async (migrationId) => {
    if (!confirm('Are you sure you want to delete this migration?')) {
      return
    }

    try {
      await deleteMigration(migrationId)
      setMigrations(migrations.filter(m => m.id !== migrationId))
    } catch (err) {
      alert('Failed to delete migration: ' + err.message)
    }
  }

  const handleViewMigration = (migration) => {
    navigate(`/migrations/${migration.id}`)
  }

  const getStatusConfig = (status) => {
    const configs = {
      DRAFT: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
      CONFIGURED: { label: 'Configured', color: 'bg-blue-100 text-blue-700' },
      READY: { label: 'Ready', color: 'bg-cyan-100 text-cyan-700' },
      IN_PROGRESS: { label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
      COMPLETED: { label: 'Completed', color: 'bg-green-100 text-green-700' },
      FAILED: { label: 'Error', color: 'bg-red-100 text-red-700' },
      PENDING_REVIEW: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700' },
      ARCHIVED: { label: 'Archived', color: 'bg-gray-100 text-gray-600' }
    }
    return configs[status] || { label: status, color: 'bg-gray-100 text-gray-700' }
  }

  const getSourceSystemLabel = (system) => {
    const labels = {
      WINBOOKS: 'Winbooks',
      SAGE_BOB_50: 'Sage BOB 50',
      HORUS: 'Horus'
    }
    return labels[system] || system
  }

  const getSourceSystemIcon = (system) => {
    const icons = {
      WINBOOKS: 'calculate',
      SAGE_BOB_50: 'description',
      HORUS: 'cloud'
    }
    return icons[system] || 'folder'
  }

  const getDestinationSystemLabel = (system) => {
    const labels = {
      ODOO_18: 'Odoo 18',
      ODOO_19: 'Odoo 19'
    }
    return labels[system] || system
  }

  // Filter migrations
  const filteredMigrations = migrations.filter(m => {
    const matchesSearch = searchQuery === '' ||
      m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getSourceSystemLabel(m.source_system).toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === 'ALL' || m.status === statusFilter
    return matchesSearch && matchesStatus
  })

  // Calculate stats
  const stats = {
    active: migrations.filter(m => ['IN_PROGRESS', 'CONFIGURED', 'READY'].includes(m.status)).length,
    completed: migrations.filter(m => m.status === 'COMPLETED').length,
    actionRequired: migrations.filter(m => ['FAILED', 'PENDING_REVIEW'].includes(m.status)).length
  }

  // Show loading while user is being fetched
  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading user data...</div>
      </div>
    )
  }

  // Show message if no active tenancy
  if (!user.active_tenancy_id) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-gray-500 mb-4">No active organization found.</p>
            <p className="text-sm text-gray-400">Please select or create an organization first.</p>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-500">Loading migrations...</div>
        </div>
      </DashboardLayout>
    )
  }

  // Show the create form
  if (showCreateForm) {
    return (
      <DashboardLayout>
        <div className="p-8 max-w-[960px] mx-auto w-full">
          {/* Breadcrumbs */}
          <div className="flex flex-wrap gap-2 py-2 mb-4">
            <button
              onClick={() => setShowCreateForm(false)}
              className="text-gray-500 text-sm font-medium hover:underline"
            >
              Migrations
            </button>
            <span className="text-gray-500 text-sm font-medium">/</span>
            <span className="text-primary text-sm font-medium">New Migration Wizard</span>
          </div>

          {/* Page Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-black text-[#111418] tracking-tight">Create New Migration</h1>
            <p className="text-gray-500 text-base mt-1">Configure the source and client details for the new accounting migration.</p>
          </div>

          {/* Wizard Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              <MigrationForm
                onSuccess={handleMigrationCreated}
                onCancel={() => setShowCreateForm(false)}
              />
            </div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="p-8 max-w-7xl mx-auto w-full space-y-6">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black text-[#111418] tracking-tight">Migrations</h1>
            <p className="text-gray-500 text-sm mt-1">Manage and track all ongoing data transfers</p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-lg font-bold text-sm shadow-sm hover:bg-primary/90 transition-all"
          >
            <span className="material-symbols-outlined text-xl">add</span>
            <span>New Migration</span>
          </button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex flex-col gap-2 rounded-xl p-6 bg-white border border-gray-200 shadow-sm">
            <div className="flex justify-between items-start">
              <p className="text-gray-500 text-sm font-medium uppercase tracking-wider">Active Migrations</p>
              <span className="material-symbols-outlined text-primary">pending_actions</span>
            </div>
            <p className="text-[#111418] text-3xl font-bold">{stats.active}</p>
            <p className="text-xs text-gray-500">Currently in progress</p>
          </div>
          <div className="flex flex-col gap-2 rounded-xl p-6 bg-white border border-gray-200 shadow-sm">
            <div className="flex justify-between items-start">
              <p className="text-gray-500 text-sm font-medium uppercase tracking-wider">Completed</p>
              <span className="material-symbols-outlined text-green-500">task_alt</span>
            </div>
            <p className="text-[#111418] text-3xl font-bold">{stats.completed}</p>
            <p className="text-xs text-gray-500">Successfully migrated</p>
          </div>
          <div className={`flex flex-col gap-2 rounded-xl p-6 bg-white border shadow-sm ${stats.actionRequired > 0 ? 'border-l-4 border-l-amber-400 border-gray-200' : 'border-gray-200'}`}>
            <div className="flex justify-between items-start">
              <p className="text-gray-500 text-sm font-medium uppercase tracking-wider">Action Required</p>
              <span className="material-symbols-outlined text-amber-500">warning</span>
            </div>
            <p className="text-[#111418] text-3xl font-bold">{stats.actionRequired}</p>
            <p className="text-xs text-amber-600 font-medium">
              {stats.actionRequired > 0 ? 'Attention needed' : 'All clear'}
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <span className="material-symbols-outlined">search</span>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 bg-[#f6f7f8] border-none rounded-lg text-sm text-[#111418] placeholder-gray-500 focus:ring-2 focus:ring-primary"
                placeholder="Search by name or source system..."
              />
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-[#f6f7f8] rounded-lg text-sm font-semibold text-gray-700 border-none focus:ring-2 focus:ring-primary"
              >
                <option value="ALL">All Statuses</option>
                <option value="DRAFT">Draft</option>
                <option value="CONFIGURED">Configured</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="FAILED">Error</option>
                <option value="PENDING_REVIEW">Pending Review</option>
              </select>
            </div>
          </div>

          {/* Active Filters */}
          {(statusFilter !== 'ALL' || searchQuery) && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
              {statusFilter !== 'ALL' && (
                <div className="flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-bold">
                  {getStatusConfig(statusFilter).label}
                  <button onClick={() => setStatusFilter('ALL')}>
                    <span className="material-symbols-outlined text-sm">close</span>
                  </button>
                </div>
              )}
              {searchQuery && (
                <div className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-bold">
                  "{searchQuery}"
                  <button onClick={() => setSearchQuery('')}>
                    <span className="material-symbols-outlined text-sm">close</span>
                  </button>
                </div>
              )}
              <button
                onClick={() => { setStatusFilter('ALL'); setSearchQuery('') }}
                className="text-xs font-bold text-primary hover:underline ml-2"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Migrations Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {hasChildren && (
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Client</th>
                  )}
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Migration Name</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Source</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Destination</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredMigrations.length === 0 ? (
                  <tr>
                    <td colSpan={hasChildren ? 7 : 6} className="px-6 py-12 text-center text-gray-500">
                      {migrations.length === 0 ? (
                        <div>
                          <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">folder_open</span>
                          <p className="font-medium">No migrations yet</p>
                          <p className="text-sm">Create your first migration to get started.</p>
                        </div>
                      ) : (
                        <div>
                          <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">search_off</span>
                          <p className="font-medium">No results found</p>
                          <p className="text-sm">Try adjusting your search or filters.</p>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredMigrations.map((migration) => {
                    const statusConfig = getStatusConfig(migration.status)
                    return (
                      <tr
                        key={migration.id}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => handleViewMigration(migration)}
                      >
                        {hasChildren && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="size-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                                {migration.client_name?.[0]?.toUpperCase() || 'C'}
                              </div>
                              <span className="text-sm font-bold text-[#111418]">
                                {migration.client_name || 'Unknown Client'}
                              </span>
                            </div>
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-[#111418]">{migration.name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span className="material-symbols-outlined text-green-600">
                              {getSourceSystemIcon(migration.source_system)}
                            </span>
                            {getSourceSystemLabel(migration.source_system)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span className="material-symbols-outlined text-primary">
                              check_circle
                            </span>
                            {getDestinationSystemLabel(migration.destination_system)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(migration.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleViewMigration(migration)
                            }}
                            className="text-primary hover:text-primary/80 font-bold text-sm"
                          >
                            {migration.status === 'FAILED' ? 'Fix Errors' : 'View Details'}
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {filteredMigrations.length > 0 && (
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-100">
              <div className="text-sm text-gray-500 font-medium">
                Showing {filteredMigrations.length} of {migrations.length} migrations
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}

export default MigrationsPage
